import { useAppStore, AppStoreState } from "@/stores/appStore";
import { createFileRoute } from "@tanstack/react-router";
import styled from "styled-components";
import { type_c, type_a, type_d, type_f } from "@/theme/typography";
import { useState } from "react";
import { useContent } from "@/hooks/useContent";

export const Route = createFileRoute("/_configurator/step-4")({
	component: Step4,
});

interface MaterialsProps {
	items: Array<{
		id: string;
		title: string;
		thumbnail?: string;
		target?: string;
	}>;
	stateType: "selectedDimensions" | "selectedModifiers" | "selectedMaterials";
	stateKey: keyof AppStoreState["configurator"];
	defaultVal: any;
}

interface Material {
	type: keyof AppStoreState["configurator"];
	materials: Array<MaterialsProps["items"][0]>;
	modifierMaterials?: Array<{
		name: string;
		materials: Array<MaterialsProps["items"][0]>;
	}>;
}

const Materials = ({ items, stateType, stateKey, defaultVal }: MaterialsProps) => {
	const [active, setActive] = useState(defaultVal);

	const updateMaterial = (
		value: MaterialsProps["items"][0],
		stateType: "selectedDimensions" | "selectedModifiers" | "selectedMaterials",
		stateKey: keyof AppStoreState["configurator"],
		id: string | undefined
	) => {
		const options = (prevState: AppStoreState) => {
			const configItem = prevState.configurator[stateKey];
			if (!("selectedDimensions" in configItem)) return prevState.configurator;

			return {
				...prevState.configurator,
				[stateKey]: {
					...configItem,
					[stateType]: {
						...configItem[stateType],
						[id || ""]: value,
					},
				},
			};
		};

		setActive(value);

		useAppStore.setState((prevState) => ({
			configurator: options(prevState),
		}));
	};

	const MaterialsWrapper = styled.div`
		display: grid;
		grid-template-columns: repeat(3, minMax(0, 1fr));
		row-gap: 0.75rem;
		column-gap: 12px;
		max-width: 400px;

		.material__item {
			display: flex;
			flex-direction: column;
			column-gap: 1rem;
			transition: background-color 98ms linear;
			cursor: pointer;

			svg {
				width: 20px;
				fill: #7d6a64;
			}
		}

		.material__item__image-wrapper {
			width: 100%;
			aspect-ratio: 1/1;
			overflow: hidden;
			border-radius: 3px;
			background-color: #e5e0df;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.material__item:not(.is_active) img {
			opacity: 0.7;
			transition: opacity 98ms linear;
		}

		.material__item:hover img {
			opacity: 1;
		}

		.material__item__title {
			font-weight: 400;
			color: #7d6a64;
			margin-top: 0.5rem;
		}

		.material__item:hover .material__item__title {
			color: ${({ theme }) => theme.colors.secondary};
		}

		.material__item.is_active .material__item__title {
			color: ${({ theme }) => theme.colors.secondary};
		}
	`;

	return (
		<MaterialsWrapper>
			{items &&
				items.map((item, i) => (
					<div
						key={i}
						onClick={() => updateMaterial(item, stateType, stateKey, item.target)}
						className={`material__item ${active.id == item.id && "is_active"}`}>
						<div className="material__item__image-wrapper">{item.thumbnail && <img src={item.thumbnail} />}</div>

						<p className="material__item__title" style={type_f}>
							{item.title}
						</p>
					</div>
				))}
		</MaterialsWrapper>
	);
};

function Step4() {
	const { gate, pillar } = useAppStore((state) => state.configurator);
	const { getMaterials, getGates, getPillars } = useContent();
	const currentGate = { ...getGates().find((x) => x.id === gate.id), ...gate };
	const currentPillar = { ...getPillars().find((x) => x.id === pillar.id), ...pillar };

	const materials = getMaterials([currentGate, currentPillar]);

	return (
		<Wrapper>
			<p className="step__number" style={type_c}>
				Step 3
			</p>
			<h4 className="step__title" style={type_a}>
				Materials
			</h4>
			<div className="section_wrapper">
				{materials.map((material: Material, index: number) => {
					return (
						<Section key={index}>
							<p className="step__subtitle" style={type_d}>
								{material.type}
							</p>
							<Materials
								defaultVal={""}
								items={material.materials}
								stateKey={material.type}
								stateType={"selectedMaterials"}
							/>

							{material.modifierMaterials &&
								material.modifierMaterials.map(
									(modifierMaterial) =>
										modifierMaterial.materials.length > 0 && (
											<Modifiers key={modifierMaterial.name}>
												<p className="step__subtitle" style={type_d}>
													{modifierMaterial.name}
												</p>
												<Materials
													defaultVal={""}
													items={modifierMaterial.materials}
													stateKey={material.type}
													stateType={"selectedMaterials"}
												/>
											</Modifiers>
										)
								)}
						</Section>
					);
				})}
			</div>
		</Wrapper>
	);
}

const Wrapper = styled.div`
	padding-block: 1rem;
	padding-inline: 10px;
	height: 100%;

	.section_wrapper {
		overflow: scroll;
		padding-bottom: 80px;
		height: 100%;
		padding-block-start: 0.5rem;
	}

	& > :last-child {
		margin-bottom: 0;
	}

	.step__number {
		color: #7d6a64;
	}

	.step__title {
		padding-block-end: 0.5rem;
		color: ${({ theme }) => theme.colors.secondary};
		border-bottom: 1px solid #e7dad7;
	}

	.step__subtitle {
		margin-bottom: 0.3rem;
		font-weight: 400;
		color: #7d6a64;
		margin-bottom: 1rem;
	}
`;

const Section = styled.section`
	margin-bottom: 1rem;
	background-color: #e9e1de;
	padding-block: 1rem;
	padding-inline: 16px;
	border-radius: 5px;
`;

const Modifiers = styled.section`
	margin-bottom: 1rem;
	padding-block: 1rem;
	border-radius: 5px;
	border-top: 1px solid #d8cecb;
	margin-top: 1rem;
`;
